import React, { useEffect, useState } from "react";
import { api, BRAND, formatApiError } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Mail, Phone, MapPin, Instagram, ArrowRight } from "lucide-react";

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [instagramHref, setInstagramHref] = useState(BRAND.instagram);

  useEffect(() => {
    // Use the mobile share URL on touch devices, desktop URL elsewhere.
    try {
      const isMobile =
        typeof window !== "undefined" &&
        (window.matchMedia?.("(pointer: coarse)").matches ||
          /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent || ""));
      setInstagramHref(isMobile ? BRAND.instagram_mobile : BRAND.instagram);
    } catch (_) {
      setInstagramHref(BRAND.instagram);
    }
  }, []);

  const onChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status.state === "loading") return;
    setStatus({ state: "loading", msg: "" });
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setStatus({ state: "success", msg: t("contact.sent") });
    } catch (err) {
      setStatus({
        state: "error",
        msg: formatApiError(err?.response?.data?.detail) || t("contact.error"),
      });
    }
  };

  const wa = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(
    "Hello Tenjara Lombok, I'd like more information."
  )}`;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-sand/50 text-charcoal"
    >
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10 py-24 sm:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: copy + channels */}
          <div className="lg:col-span-5">
            <p className="overline" data-testid="contact-overline">
              — {t("contact.overline")}
            </p>
            <h2
              className="mt-6 font-heading font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight whitespace-pre-line"
              data-testid="contact-title"
            >
              {t("contact.title")}
            </h2>
            <p className="mt-8 text-base sm:text-lg text-volcanic font-light leading-relaxed max-w-lg">
              {t("contact.subtitle")}
            </p>

            <div className="mt-12">
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-button"
                className="group inline-flex items-center gap-3 bg-olive text-ivory px-8 py-4 text-[0.72rem] tracking-[0.22em] uppercase font-medium hover:bg-charcoal transition-colors duration-500"
              >
                {t("contact.whatsapp")}
                <ArrowRight
                  size={16}
                  className="transition-transform duration-500 group-hover:translate-x-1"
                />
              </a>
            </div>

            <div className="mt-14 border-t border-charcoal/15 pt-10">
              <div className="overline">— {t("contact.channels")}</div>
              <ul className="mt-6 space-y-5">
                <li className="flex items-start gap-4">
                  <Mail className="mt-1 shrink-0 text-olive" size={16} strokeWidth={1.5} />
                  <div>
                    <div className="text-[0.68rem] tracking-[0.22em] uppercase text-volcanic/70 font-medium">
                      {t("contact.email_label")}
                    </div>
                    <a
                      href={`mailto:${BRAND.email}`}
                      data-testid="contact-email"
                      className="font-heading text-xl sm:text-2xl text-charcoal hover:text-teakwood transition-colors"
                    >
                      {BRAND.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Phone className="mt-1 shrink-0 text-olive" size={16} strokeWidth={1.5} />
                  <div>
                    <div className="text-[0.68rem] tracking-[0.22em] uppercase text-volcanic/70 font-medium">
                      {t("contact.phone_label")}
                    </div>
                    <a
                      href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
                      data-testid="contact-phone"
                      className="font-heading text-xl sm:text-2xl text-charcoal hover:text-teakwood transition-colors"
                    >
                      {BRAND.phone}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin className="mt-1 shrink-0 text-olive" size={16} strokeWidth={1.5} />
                  <div>
                    <div className="text-[0.68rem] tracking-[0.22em] uppercase text-volcanic/70 font-medium">
                      {t("contact.address")}
                    </div>
                    <div
                      data-testid="contact-address"
                      className="font-heading text-xl sm:text-2xl text-charcoal leading-snug"
                    >
                      {BRAND.address}
                    </div>
                  </div>
                </li>
              </ul>

              <div className="mt-10 flex items-center gap-4">
                <div className="overline">— {t("contact.social")}</div>
                <a
                  href={instagramHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  data-testid="social-instagram"
                  className="inline-flex items-center gap-3 border border-charcoal/20 pl-2 pr-4 py-2 hover:bg-charcoal hover:text-ivory hover:border-charcoal transition-colors group"
                >
                  <span className="w-8 h-8 flex items-center justify-center">
                    <Instagram size={16} strokeWidth={1.5} />
                  </span>
                  <span className="font-heading text-lg tracking-tight leading-none">
                    @tenjaralombok
                  </span>
                </a>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-6 lg:col-start-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-ivory p-8 sm:p-12 border border-charcoal/10"
              noValidate
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="sm:col-span-2">
                  <label className="overline block mb-2" htmlFor="c-name">
                    {t("contact.name")}*
                  </label>
                  <input
                    id="c-name"
                    required
                    data-testid="contact-name"
                    className="editorial-input"
                    value={form.name}
                    onChange={onChange("name")}
                  />
                </div>
                <div>
                  <label className="overline block mb-2" htmlFor="c-email">
                    {t("contact.email")}*
                  </label>
                  <input
                    id="c-email"
                    type="email"
                    required
                    data-testid="contact-email-input"
                    className="editorial-input"
                    value={form.email}
                    onChange={onChange("email")}
                  />
                </div>
                <div>
                  <label className="overline block mb-2" htmlFor="c-phone">
                    {t("contact.phone")}
                  </label>
                  <input
                    id="c-phone"
                    data-testid="contact-phone-input"
                    className="editorial-input"
                    value={form.phone}
                    onChange={onChange("phone")}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-2" htmlFor="c-subject">
                    {t("contact.subject")}
                  </label>
                  <input
                    id="c-subject"
                    data-testid="contact-subject"
                    className="editorial-input"
                    value={form.subject}
                    onChange={onChange("subject")}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-2" htmlFor="c-message">
                    {t("contact.message")}*
                  </label>
                  <textarea
                    id="c-message"
                    required
                    data-testid="contact-message"
                    className="editorial-input"
                    rows={5}
                    value={form.message}
                    onChange={onChange("message")}
                  />
                </div>
              </div>

              <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={status.state === "loading"}
                  className="group inline-flex items-center gap-3 bg-charcoal text-ivory px-8 py-4 text-[0.72rem] tracking-[0.22em] uppercase font-medium hover:bg-teakwood transition-colors duration-500 disabled:opacity-60"
                >
                  {status.state === "loading" ? t("contact.sending") : t("contact.send")}
                  <ArrowRight
                    size={16}
                    className="transition-transform duration-500 group-hover:translate-x-1"
                  />
                </button>

                {status.state === "success" && (
                  <div
                    className="text-sm text-olive font-medium"
                    data-testid="contact-success"
                  >
                    {status.msg}
                  </div>
                )}
                {status.state === "error" && (
                  <div
                    className="text-sm text-destructive font-medium"
                    data-testid="contact-error"
                  >
                    {status.msg}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
